import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import Card from '@components-clean/core/card';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import ToggleShowHistoryIcon from '@components-clean/core/grid/show-history-icon';
import Grid from '@components-clean/core/grid-fe';
import { YesNoCell, DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import AddEditSubjectTagsDialog from '@pages-clean/subject-monitoring/subject-hub/components/dialogs/add-edit-subject-tags-dialog';
import SubjectMonitoringService from '@pages-clean/subject-monitoring/subject-hub/subject-monitoring.service';
import { useBoolean } from '@hooks/index';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import './styles.scss';

interface SubjectProfileTagGridProps {
    canEdit: boolean;
    studyRef: string;
    subjectCode: string;
    datasetId: number;
}

interface ITableInfo {
    columns: GridColumnProps[];
    data: { [key: string]: unknown }[];
}

const SubjectProfileTagsGrid = ({ canEdit, subjectCode, studyRef, datasetId }: SubjectProfileTagGridProps) => {
    const showAddDialog = useBoolean(false);

    const { t } = useTranslation();
    const { t: subjectMonitoringTranslation } = useTranslation('sm');
    const { TooltipCell } = createGridCustomCells();
    const [datasetDates, setDatasetDates] = useState<string[]>([]);
    const showHistory = useBoolean(false);
    // Filter tracked manually since the grid is unmounted and re-mounted when the query refetches, which occurs when `showHistory` changes
    const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor>();

    const {
        data,
        trigger: getSelectedTagsForSubject,
        isMutating: isLoading,
    } = SubjectMonitoringAPI.useGetSubjectTagsGrid(studyRef, subjectCode, showHistory.value);

    const reloadGrid = () =>
        getSelectedTagsForSubject()
            .then((loadedData) => {
                const dates = loadedData
                    .map((review) => review.datasetDate!)
                    .filter((value, index, array) => array.indexOf(value) === index) // unique
                    .sort((a, b) => (new Date(a) > new Date(b) ? -1 : 1)); // descending
                setDatasetDates(dates);
            })
            .catch((err) => window.console.log(err));

    useEffect(() => {
        const subscribeDefault = SubjectMonitoringService.onRefreshTagGrid().subscribe((refreshData) => {
            if (refreshData) {
                reloadGrid().catch((err) => window.console.log(err));
            }
        });

        return () => subscribeDefault.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        reloadGrid().catch((err) => window.console.log(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [subjectCode, showHistory.value]);

    const tableInfo = useMemo(() => {
        if (!data) {
            return undefined;
        }
        const tooltipCellTwoLine = TooltipCell({
            isCsv: false,
            maxLines: 2,
        });

        const info: ITableInfo = {
            columns: [
                {
                    id: 'removed',
                    field: 'removed',
                    title: subjectMonitoringTranslation('grid.tags.columns.removed'),
                    width: 80,
                    minWidth: 80,
                    cells: { data: YesNoCell },
                    filter: 'boolean',
                },
                {
                    id: 'parentTagActive',
                    field: 'parentTagActive',
                    title: subjectMonitoringTranslation('grid.tags.columns.parentTagActive'),
                    minWidth: 80,
                    cells: { data: YesNoCell },
                    filter: 'boolean',
                },
                {
                    id: 'label',
                    field: 'label',
                    title: subjectMonitoringTranslation('grid.tags.columns.label'),
                    width: 1,
                    minWidth: 80,
                    cells: { data: tooltipCellTwoLine },
                },
                {
                    id: 'datasetDate',
                    field: 'datasetDate',
                    title: subjectMonitoringTranslation('grid.tags.columns.datasetDate'),
                    minWidth: 80,
                    lovData: datasetDates,
                },
                {
                    id: 'addedOn',
                    field: 'addedOn',
                    title: subjectMonitoringTranslation('grid.tags.columns.addedOn'),
                    minWidth: 80,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    id: 'addedBy',
                    field: 'addedBy',
                    title: subjectMonitoringTranslation('grid.tags.columns.addedBy'),
                    minWidth: 80,
                    cells: { data: tooltipCellTwoLine },
                },
                {
                    id: 'updatedOn',
                    field: 'updatedOn',
                    title: subjectMonitoringTranslation('grid.tags.columns.updatedOn'),
                    minWidth: 80,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    id: 'updatedBy',
                    field: 'updatedBy',
                    title: subjectMonitoringTranslation('grid.tags.columns.updatedBy'),
                    minWidth: 80,
                    cells: { data: tooltipCellTwoLine },
                },
            ],
            data,
        };

        return info;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    if (!tableInfo || isLoading) {
        return (
            <Card
                className="placeholder-card"
                data-testid="grid"
                title={subjectMonitoringTranslation('grid.tags.title')}
            >
                {isLoading ? <Loading /> : <InfoField message={t('emptyMessage')} type="Info" layout="center" />}
            </Card>
        );
    }

    const addCallback = () => {
        showAddDialog.setTrue();
    };

    const onAddClose = () => {
        showAddDialog.setFalse();
    };

    const onSubmitHandler = async () => {
        showAddDialog.setFalse();
        await reloadGrid();
        SubjectMonitoringService.refreshManageTagGrid(true);
    };

    const customGridMenuItems: GridMenuItemProps[] = [
        {
            title: showHistory.value
                ? subjectMonitoringTranslation('grid.tags.hideHistory')
                : subjectMonitoringTranslation('grid.tags.showHistory'),
            'data-testid': 'riskMonitoring-grid-show-deleted-records',
            customIcon: <ToggleShowHistoryIcon showHistory={showHistory.value} />,
            onClick: () => {
                showHistory.toggle();
            },
        },
    ];

    return (
        <>
            {showAddDialog.value && (
                <AddEditSubjectTagsDialog
                    studyRef={studyRef}
                    subjectCodes={[subjectCode]}
                    onClose={onAddClose}
                    onSubmit={onSubmitHandler}
                    datasetId={datasetId}
                    isEditSubject
                    isSubjectProfile
                />
            )}
            <div className="tag-grid">
                <Grid
                    dataItemKey="id"
                    title={subjectMonitoringTranslation('grid.tags.title')}
                    maxHeight="auto"
                    testDataId="subject-profile-tags-grid"
                    data={tableInfo.data}
                    columns={tableInfo.columns}
                    loading={isLoading}
                    useLoadingOverlay
                    gridSettingsMenuDisabled
                    canEdit={canEdit}
                    selectable={{ enabled: true, mode: 'single' }}
                    gridDownloadDisabled
                    rowReorder={false}
                    addCallback={addCallback}
                    showAddIcon={canEdit}
                    customGridMenuItems={customGridMenuItems}
                    scrollable="virtual"
                    filter={gridFilter}
                    filterListener={setGridFilter}
                />
            </div>
        </>
    );
};

export default SubjectProfileTagsGrid;
